import { useEffect, useMemo, type FC } from "react";
import { type CustomHighlight } from "../PdfTemplate";
import ListTabBar from "../../molecules/ListTabBar";
import { HorizontalSpacer } from "../../atoms/Spacers";
import EditValidationFields from "./EditValidationFields";
import EditMandatoryFields from "./EditMandatoryFields";
import EditOptionalFields from "./EditOptionalFields";
import EditInvoiceRowsField from "./EditInvoiceRowsField";
import EditStamps from "./EditStamps";
import EditMasks from "./EditMasks";
import EditSettings from "./EditSettings";
import type {
  InvoiceReaderTemplateFieldExtractionModel,
  InvoiceReaderTemplateRowExtractionModel,
  InvoiceReaderTemplateType,
  InvoiceReaderVerifierPositionsModel,
  PossibleFieldPositionWithValue,
  StampInformation,
} from "../../../models/ClientInvoiceReaderTemplateModel";
import { Hidden, Visible } from "react-grid-system";
import Select from "../../atoms/inputs/Select";
import type {
  TemplateTabHandle,
  EditableMask,
} from "../../../models/Templates";
import type { TabId } from "../../templates/EditTemplatePage";
import type { SaveHandle } from "../../../hooks/useSaveManager";
import { useSearchParams } from "react-router-dom";
import type { TemplateSettingsInput } from "../../../models/InvoiceInterpretationInputs";
import { SettingsIconSmall } from "../../atoms/icons/SettingsIcon";

interface Props {
  possibleFields: Array<PossibleFieldPositionWithValue>;
  verifierPositions?: InvoiceReaderVerifierPositionsModel;
  highlightedFields: Array<CustomHighlight>;
  setHighlightedFields: React.Dispatch<React.SetStateAction<CustomHighlight[]>>;
  onToggleDisplayHighlights: () => void;
  fields: Array<InvoiceReaderTemplateFieldExtractionModel>;
  rowExtractionData: Array<InvoiceReaderTemplateRowExtractionModel>;
  stampInformation?: StampInformation;
  setStampInformation?: React.Dispatch<
    React.SetStateAction<StampInformation | undefined>
  >;
  maskingData: Array<EditableMask>;
  templateSettings?: TemplateSettingsInput;
  templateType?: InvoiceReaderTemplateType;
  refetchTemplate?: () => void;
  tabRefs: React.RefObject<Record<TabId, TemplateTabHandle | null>>;
  saveManager: {
    register: (key: TabId, ref: SaveHandle | null) => void;
  };
  onUpdateMaskingDataState: React.Dispatch<
    React.SetStateAction<EditableMask[]>
  >;
  onUpdateActiveMaskId?: (id: string | null) => void;
  activeMaskId?: string | null;
  onTabDirtyChange: () => void;
  onNavigateToPage?: (page: number) => void;
  totalPages?: number;
  onSaveError?: (message: string) => void;
  ignoredHeaders?: string[];
}

const tabs = [
  {
    id: "templateValidation",
    tabLabelKey: "templates.validationFields",
    color: "rgba(156, 39, 176, 0.8)",
  },
  {
    id: "mandatoryInvoiceFields",
    tabLabelKey: "templates.mandatoryFields",
    color: "rgba(233, 3, 58, 0.8)",
  },
  {
    id: "optionalFields",
    tabLabelKey: "templates.optionalFields",
    color: "rgba(0, 123, 255, 0.8)",
  },
  {
    id: "rowInterpretation",
    tabLabelKey: "templates.rowInterpretation",
    color: "rgba(255, 152, 0, 0.8)",
  },
  {
    id: "stamp",
    tabLabelKey: "templates.stamp",
    color: "#4FAE9D",
  },
  {
    id: "mask",
    tabLabelKey: "templates.mask",
    color: "#E48FB1",
  },
  {
    id: "settings",
    tabLabel: <SettingsIconSmall />,
  },
];
const tabsSmallScreen = [
  {
    value: "templateValidation",
    label: "templates.validationFields",
  },
  {
    value: "mandatoryInvoiceFields",
    label: "templates.mandatoryFields",
  },
  {
    value: "optionalFields",
    label: "templates.optionalFields",
  },
  {
    value: "rowInterpretation",
    label: "templates.rowInterpretation",
  },
  {
    value: "stamp",
    label: "templates.stamp",
  },
  {
    value: "mask",
    label: "templates.mask",
  },
  {
    value: "settings",
    label: "clientCard.settings",
  },
];

const EditTemplateFields: FC<Props> = ({
  possibleFields,
  verifierPositions,
  highlightedFields,
  setHighlightedFields,
  onToggleDisplayHighlights,
  fields,
  rowExtractionData,
  stampInformation,
  setStampInformation,
  maskingData,
  templateSettings,
  templateType,
  refetchTemplate,
  tabRefs,
  saveManager,
  onUpdateMaskingDataState,
  onUpdateActiveMaskId,
  activeMaskId,
  onTabDirtyChange,
  onNavigateToPage,
  totalPages,
  onSaveError,
  ignoredHeaders,
}) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const showStampAndMask =
    templateType === "DEBIT" || templateType === "DEBIT_MASK_AND_STAMP_ONLY";
  const showOptionalAndRows = templateType !== "DEBIT_MASK_AND_STAMP_ONLY";

  const visibleTabIds = new Set<TabId>([
    "templateValidation",
    "mandatoryInvoiceFields",
    ...(showOptionalAndRows
      ? (["optionalFields", "rowInterpretation"] as TabId[])
      : []),
    ...(showStampAndMask ? (["stamp", "mask"] as TabId[]) : []),
    "settings",
  ]);

  const visibleTabs = tabs.filter((t) => visibleTabIds.has(t.id as TabId));
  const visibleTabsSmallScreen = tabsSmallScreen.filter((t) =>
    visibleTabIds.has(t.value as TabId),
  );

  const isValidTab = (tab: string | null): tab is TabId =>
    tab !== null && visibleTabIds.has(tab as TabId);
  const selectedTab: TabId = (() => {
    const tab = searchParams.get("tab");
    return isValidTab(tab) ? tab : "templateValidation";
  })();

  useEffect(() => {
    const tab = searchParams.get("tab");

    if (!isValidTab(tab)) {
      setSearchParams({ tab: "templateValidation" }, { replace: true });
    }
  }, [visibleTabIds.size]);

  const onSelectTab = (tab: TabId) => {
    setSearchParams({ tab }, { replace: true });
  };

  const mandatoryFields = useMemo(
    () => fields.filter((f) => f.isRequired),
    [fields],
  );
  const optionalFields = useMemo(
    () => fields.filter((f) => !f.isRequired),
    [fields],
  );

  const nextVisibleTab = (after: TabId): TabId | undefined => {
    const allTabIds = tabs.map((t) => t.id as TabId);
    const idx = allTabIds.indexOf(after);
    return allTabIds.slice(idx + 1).find((id) => visibleTabIds.has(id));
  };

  const onSaveAndContinue = (tabId?: TabId) => {
    if (refetchTemplate) refetchTemplate();
    if (tabId) {
      const target = visibleTabIds.has(tabId) ? tabId : nextVisibleTab(tabId);
      if (target) onSelectTab(target);
    }
  };

  const getRef = (id: TabId) => (instance: TemplateTabHandle | null) => {
    tabRefs.current[id] = instance;
    if (instance) saveManager.register(id, instance);
  };

  return (
    <div>
      <Hidden xs sm md>
        <ListTabBar
          tabs={visibleTabs}
          selectedId={selectedTab}
          onSelectTabId={onSelectTab}
          small
          fullWidth
        />
      </Hidden>
      <Visible xs sm md>
        <Select
          options={visibleTabsSmallScreen}
          value={selectedTab}
          onChange={(value: string) => onSelectTab(value as TabId)}
        />
      </Visible>

      <HorizontalSpacer spacing={2} />
      <div
        style={{
          display: selectedTab === "templateValidation" ? "block" : "none",
        }}
      >
        <EditValidationFields
          ref={getRef("templateValidation")}
          possibleFields={possibleFields}
          highlightedFields={highlightedFields}
          setHighlightedFields={setHighlightedFields}
          verifierPositions={verifierPositions}
          onSaveAndContinue={() => onSaveAndContinue("mandatoryInvoiceFields")}
          onDirtyChange={onTabDirtyChange}
          onNavigateToPage={onNavigateToPage}
          totalPages={totalPages}
          onSaveError={onSaveError}
        />
      </div>

      <div
        style={{
          display: selectedTab === "mandatoryInvoiceFields" ? "block" : "none",
        }}
      >
        <EditMandatoryFields
          ref={getRef("mandatoryInvoiceFields")}
          possibleFields={possibleFields}
          highlightedFields={highlightedFields}
          setHighlightedFields={setHighlightedFields}
          mandatoryFields={mandatoryFields}
          onSaveAndContinue={() => onSaveAndContinue("optionalFields")}
          onDirtyChange={onTabDirtyChange}
          onNavigateToPage={onNavigateToPage}
          totalPages={totalPages}
          onSaveError={onSaveError}
        />
      </div>

      {visibleTabIds.has("optionalFields") && (
        <div
          style={{
            display: selectedTab === "optionalFields" ? "block" : "none",
          }}
        >
          <EditOptionalFields
            ref={getRef("optionalFields")}
            possibleFields={possibleFields}
            highlightedFields={highlightedFields}
            setHighlightedFields={setHighlightedFields}
            optionalFields={optionalFields}
            onSaveAndContinue={() => onSaveAndContinue("rowInterpretation")}
            onDirtyChange={onTabDirtyChange}
            onNavigateToPage={onNavigateToPage}
            totalPages={totalPages}
            onSaveError={onSaveError}
          />
        </div>
      )}

      {visibleTabIds.has("rowInterpretation") && (
        <div
          style={{
            display: selectedTab === "rowInterpretation" ? "block" : "none",
          }}
        >
          <EditInvoiceRowsField
            ref={getRef("rowInterpretation")}
            possibleFields={possibleFields}
            highlightedFields={highlightedFields}
            setHighlightedFields={setHighlightedFields}
            rowExtractionData={rowExtractionData}
            onSaveAndContinue={() => onSaveAndContinue("stamp")}
            onDirtyChange={onTabDirtyChange}
            onNavigateToPage={onNavigateToPage}
            totalPages={totalPages}
            onSaveError={onSaveError}
            ignoredHeaders={ignoredHeaders}
          />
        </div>
      )}

      {visibleTabIds.has("stamp") && (
        <div style={{ display: selectedTab === "stamp" ? "block" : "none" }}>
          <EditStamps
            ref={getRef("stamp")}
            possibleFields={possibleFields}
            highlightedFields={highlightedFields}
            setHighlightedFields={setHighlightedFields}
            stampInformation={stampInformation}
            onSaveAndContinue={() => onSaveAndContinue("mask")}
            setStampInformation={setStampInformation}
            onDirtyChange={onTabDirtyChange}
            onNavigateToPage={onNavigateToPage}
            totalPages={totalPages}
            onSaveError={onSaveError}
          />
        </div>
      )}

      {visibleTabIds.has("mask") && (
        <div style={{ display: selectedTab === "mask" ? "block" : "none" }}>
          <EditMasks
            ref={getRef("mask")}
            possibleFields={possibleFields}
            highlightedFields={highlightedFields}
            setHighlightedFields={setHighlightedFields}
            maskingData={maskingData}
            onSaveAndContinue={() => onSaveAndContinue("settings")}
            onUpdateMaskingDataState={onUpdateMaskingDataState}
            activeMaskId={activeMaskId}
            onUpdateActiveMaskId={onUpdateActiveMaskId}
            onDirtyChange={onTabDirtyChange}
            onNavigateToPage={onNavigateToPage}
            totalPages={totalPages}
          />
        </div>
      )}

      <div style={{ display: selectedTab === "settings" ? "block" : "none" }}>
        <EditSettings
          onToggleDisplayHighlights={onToggleDisplayHighlights}
          templateSettings={templateSettings}
        />
      </div>
    </div>
  );
};

export default EditTemplateFields;
